import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithGoogle, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-violet-950 via-purple-900 to-gray-950">
        <div className="absolute inset-0">
          {/* Floating database icons */}
          <div className="absolute top-20 left-10 animate-float">
            <div className="w-20 h-20 bg-violet-600/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-3xl">
              🗄️
            </div>
          </div>
          <div className="absolute top-40 right-20 animate-float-slow">
            <div className="w-16 h-16 bg-purple-600/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-2xl">
              💾
            </div>
          </div>
          <div className="absolute bottom-40 left-1/4 animate-float-slower">
            <div className="w-24 h-24 bg-violet-700/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-4xl">
              🗃️
            </div>
          </div>
          <div className="absolute top-1/3 right-1/3 animate-pulse-slow">
            <div className="w-32 h-32 bg-gradient-radial from-violet-600/30 to-transparent rounded-full blur-xl"></div>
          </div>
          <div className="absolute bottom-1/4 right-1/4 animate-pulse-slow" style={{ animationDelay: '2s' }}>
            <div className="w-40 h-40 bg-gradient-radial from-purple-600/30 to-transparent rounded-full blur-xl"></div>
          </div>
        </div>
      </div>

      {/* Navbar */}
      <nav className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-3xl">🗄️</span>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
              DB2XL
            </h1>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="text-gray-300 hover:text-white transition-colors"
          >
            Back to Home
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="card-gradient p-8">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <span className="text-4xl">🗄️</span>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                  DB2XL
                </h2>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Welcome Back
              </h3>
              <p className="text-gray-300">
                Access your database management tools
              </p>
            </div>
            
            {error && (
              <div className="rounded-md bg-red-900/50 border border-red-700/50 p-4 mb-6">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-400">
                      Authentication Error
                    </h3>
                    <div className="mt-2 text-sm text-red-300">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign in with Google
                </>
              )}
            </button>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                New to DB2XL?{' '}
                <button
                  onClick={() => navigate('/')}
                  className="text-violet-400 hover:text-violet-300 font-medium"
                >
                  Learn more
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;